import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Badge, Button, EmptyState, cn } from '@vta/ui';
import type { Program } from '@vta/types';
import { formatProgramLabel, formatTeamNumber, programBadgeVariant } from '../../lib/format.ts';

const VIRTUALIZATION_THRESHOLD = 100;
const PAGE_SIZE = 50;

/**
 * Team list for the city page. We don't depend on @tanstack/react-virtual
 * because it isn't in package.json (deps are locked for this chunk). The
 * spec accepts a "show 50 + load-more" pattern for >100 entries.
 *
 * The shape is intentionally a structural subset of TeamSummary because the
 * /api/cities response projects fewer columns than TeamSummary specifies.
 */
export interface CityTeamRow {
  teamNumber: string;
  teamName: string;
  organization?: string | null;
  program: Program;
  city?: string | null;
  country?: string | null;
  hasStory: boolean;
}

export interface CityTeamListProps {
  teams: CityTeamRow[];
  className?: string;
}

export function CityTeamList({ teams, className }: CityTeamListProps) {
  const [shown, setShown] = useState(PAGE_SIZE);

  const needsPaging = teams.length > VIRTUALIZATION_THRESHOLD;
  const visible = useMemo(
    () => (needsPaging ? teams.slice(0, shown) : teams),
    [teams, shown, needsPaging],
  );

  if (teams.length === 0) {
    return (
      <EmptyState
        title="No teams in this city"
        description="If you know a team here, please submit a story to help us catalogue them."
        className={className}
      />
    );
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <ul className="divide-y divide-border-subtle border-y border-border-subtle">
        {visible.map((t) => (
          <li
            key={t.teamNumber}
            className="flex items-center justify-between gap-3 py-3"
          >
            <Link
              to="/team/$teamNumber"
              params={{ teamNumber: t.teamNumber }}
              className="flex min-w-0 flex-col"
            >
              <span className="font-mono text-sm text-fg">{formatTeamNumber(t.teamNumber)}</span>
              <span className="truncate text-xs text-fg-muted">{t.teamName}</span>
              {t.organization ? (
                <span className="truncate text-2xs text-fg-subtle">{t.organization}</span>
              ) : null}
            </Link>
            <div className="flex items-center gap-2">
              <Badge variant={programBadgeVariant(t.program)}>
                {formatProgramLabel(t.program, 'short')}
              </Badge>
              {t.hasStory ? <Badge variant="accent">Story</Badge> : null}
            </div>
          </li>
        ))}
      </ul>

      {needsPaging && shown < teams.length ? (
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-fg-subtle">
            Showing {visible.length} of {teams.length}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShown((s) => Math.min(teams.length, s + PAGE_SIZE))}
          >
            Load more
          </Button>
        </div>
      ) : null}
    </div>
  );
}
